import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, User, LogOut, ChevronDown } from "lucide-react";

const PHONE_NUMBER = "+919068155122";

const navItems = [
  { href: "/", label: "Home" },
  {
    label: "Services",
    children: [
      { href: "/services", label: "All 17 Embroidery Techniques" },
      { href: "/gallery", label: "Embroidery Gallery" },
      { href: "/lehenga-inspiration", label: "Lehenga Inspiration" },
      { href: "/pricing-guide", label: "Pricing Guide" },
      { href: "/estimate", label: "Cost Estimator" },
      { href: "/behind-the-craft", label: "Behind the Craft" },
    ],
  },
  {
    label: "Bridal & Lehenga",
    children: [
      { href: "/custom-bridal-lehengas", label: "Bridal Lehenga Embroidery" },
      { href: "/get-your-lehenga", label: "Get Your Lehenga Made" },
      { href: "/custom-blouses-dupattas", label: "Blouses & Dupattas" },
      { href: "/how-it-works", label: "How It Works" },
    ],
  },
  {
    label: "For Business",
    children: [
      { href: "/for-designers", label: "For Designers & Brands" },
      { href: "/start-your-collection", label: "Start Your Collection" },
      { href: "/b2b", label: "B2B / Bulk Orders" },
      { href: "/international-orders", label: "International Orders" },
      { href: "/global-clients", label: "Global Clients" },
      { href: "/wedding-planner-guide", label: "Wedding Planner Referral" },
    ],
  },
  {
    label: "About",
    children: [
      { href: "/about", label: "About KarigariHub" },
      { href: "/blog", label: "Blog" },
      { href: "/faq", label: "FAQ" },
    ],
  },
  { href: "/contact", label: "Contact" },
];

function Dropdown({ item, pathname, onNavigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timeoutRef = useRef(null);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const isActive = item.children?.some(c => pathname === c.href);

  return (
    <div ref={ref} className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        className={`flex items-center gap-1 text-[13px] tracking-wide transition-colors py-1 ${isActive ? 'text-[#D4AF37] font-medium' : 'text-foreground/70 hover:text-foreground'}`}
        onClick={() => setOpen(!open)}
        data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {item.label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-2 z-50" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
          <div className="bg-white border border-border rounded-lg shadow-lg py-2 min-w-[220px]">
            {item.children.map(child => (
              <Link
                key={child.href}
                to={child.href}
                onClick={() => { setOpen(false); onNavigate?.(); }}
                className={`block px-4 py-2 text-[13px] transition-colors ${pathname === child.href ? 'text-[#D4AF37] bg-[#D4AF37]/5 font-medium' : 'text-foreground/70 hover:text-foreground hover:bg-[#F9F9F7]'}`}
                data-testid={`nav-link-${child.href.replace(/\//g, '').replace(/-/g, '-')}`}
              >
                {child.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem("userToken");
  const userRole = localStorage.getItem("userRole");
  const userName = localStorage.getItem("userName");

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userName");
    navigate("/login");
  };

  const dashboardPath = userRole === "karigar" ? "/karigar-dashboard" : "/dashboard";
  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-40 bg-[#F9F9F7]/95 backdrop-blur-sm border-b border-border">
      <div className="container-main">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex flex-col shrink-0" data-testid="nav-logo">
            <span className="font-heading text-lg font-semibold tracking-tight">
              Karigari<span className="text-[#D4AF37]">Hub</span>
            </span>
            <span className="text-[9px] text-muted-foreground -mt-0.5 tracking-wide">Premium Hand Embroidery</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-5">
            {navItems.map((item) =>
              item.children ? (
                <Dropdown key={item.label} item={item} pathname={location.pathname} />
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`text-[13px] tracking-wide transition-colors py-1 ${location.pathname === item.href ? 'text-[#D4AF37] font-medium' : 'text-foreground/70 hover:text-foreground'}`}
                  data-testid={`nav-link-${item.href.replace('/', '') || 'home'}`}
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-phone">
              <Phone className="w-3.5 h-3.5" />
              <span>{PHONE_NUMBER}</span>
            </a>
            <span className="w-px h-4 bg-border" />
            {isLoggedIn ? (
              <div className="flex items-center gap-2">
                <Link to={dashboardPath} className="flex items-center gap-1 text-xs text-[#D4AF37] hover:text-[#D4AF37]/80 font-medium" data-testid="nav-dashboard">
                  <User className="w-3.5 h-3.5" />{userName?.split(' ')[0] || 'Dashboard'}
                </Link>
                <button onClick={handleLogout} className="text-muted-foreground hover:text-red-600 transition-colors" data-testid="nav-logout">
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <Link to="/login" className="flex items-center gap-1 text-xs text-[#D4AF37] hover:text-[#D4AF37]/80 font-medium" data-testid="nav-login">
                <User className="w-3.5 h-3.5" />Login
              </Link>
            )}
          </div>

          {/* Mobile toggle */}
          <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn" aria-label="Toggle menu">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              {navItems.map((item) =>
                item.children ? (
                  <div key={item.label}>
                    <button
                      onClick={() => setMobileExpanded(mobileExpanded === item.label ? "" : item.label)}
                      className="flex items-center justify-between w-full py-2.5 px-1 text-sm text-foreground/80"
                    >
                      {item.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === item.label ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileExpanded === item.label && (
                      <div className="pl-4 pb-2 space-y-1">
                        {item.children.map(child => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={`block py-2 text-sm ${location.pathname === child.href ? 'text-[#D4AF37] font-medium' : 'text-muted-foreground'}`}
                            onClick={closeMobile}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`py-2.5 px-1 text-sm ${location.pathname === item.href ? 'text-[#D4AF37] font-medium' : 'text-foreground/80'}`}
                    onClick={closeMobile}
                  >
                    {item.label}
                  </Link>
                )
              )}
              <div className="border-t border-border mt-2 pt-2">
                {isLoggedIn ? (
                  <>
                    <Link to={dashboardPath} className="block py-2.5 px-1 text-sm text-[#D4AF37] font-medium" onClick={closeMobile}>My Dashboard</Link>
                    <button className="py-2.5 px-1 text-sm text-red-600 text-left w-full" onClick={() => { handleLogout(); closeMobile(); }}>Logout</button>
                  </>
                ) : (
                  <Link to="/login" className="block py-2.5 px-1 text-sm text-[#D4AF37] font-medium" onClick={closeMobile}>Login / Sign Up</Link>
                )}
                <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-2 py-2.5 px-1 text-sm text-muted-foreground">
                  <Phone className="w-4 h-4" />{PHONE_NUMBER}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
